import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { authQueryOptions } from "@/lib/core/auth";
import {
    experimentArmProperty,
    experimentArmsQueryOptions,
} from "./experiment-arm.ts";
import { recordExperimentExposure } from "./record-experiment-exposure.ts";

function useAppUserId() {
    return (
        useQuery({ ...authQueryOptions(), enabled: false }).data?.appUserId ??
        null
    );
}

export function ExperimentArmsWarmup() {
    useQuery(experimentArmsQueryOptions(useAppUserId()));
    return null;
}

function useExperimentExposure(
    name: string,
    appUserId: string | null,
    exposed: boolean,
) {
    const posthog = usePostHog();
    const queryClient = useQueryClient();
    useQuery({
        queryKey: ["experiment-exposure", name, appUserId] as const,
        queryFn: exposed
            ? async () => {
                  const arm = await recordExperimentExposure({
                      data: { name },
                  });
                  if (arm) {
                      posthog.register({ [experimentArmProperty(name)]: arm });
                      queryClient.setQueryData(
                          experimentArmsQueryOptions(appUserId).queryKey,
                          (arms) => arms && { ...arms, [name]: arm },
                      );
                  }
                  return arm;
              }
            : skipToken,
        retry: 2,
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
    });
}

export function usePreviewedExperiment(
    name: string,
    {
        eligible,
        measured,
        preview,
    }: { eligible: boolean; measured: boolean; preview: string | null },
): string | null {
    const appUserId = useAppUserId();
    const arms = useQuery({
        ...experimentArmsQueryOptions(appUserId),
        enabled: eligible,
    }).data;
    const assigned = arms?.[name] ?? null;
    useExperimentExposure(
        name,
        appUserId,
        eligible && measured && preview === null && assigned !== null,
    );
    return eligible ? (preview ?? assigned) : null;
}
